import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { UserPlus, Plus, Trash2, Copy, ExternalLink, DollarSign, Ticket } from 'lucide-react';
import { toast } from 'sonner';
import AdminLayout from '../../components/AdminLayout';
import { SidebarProvider } from '../../components/AdminSidebarNew';
import EventSubNav from '../../components/EventSubNav';
import { eventsApi } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Label } from '../../components/ui/label';

const EventVendorsPage = () => {
  const { eventId } = useParams();
  const [event, setEvent] = useState(null);
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({
    vendor_name: '',
    commission_percentage: 0
  });

  useEffect(() => {
    fetchData();
  }, [eventId]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [eventRes, vendorsRes] = await Promise.all([
        eventsApi.getById(eventId),
        eventsApi.getVendorLinks(eventId),
      ]);
      setEvent(eventRes.data);
      setVendors(vendorsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Error al cargar vendedores');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!formData.vendor_name.trim()) {
      toast.error('Ingresa el nombre del vendedor');
      return;
    }
    try {
      await eventsApi.createVendorLink(eventId, {
        ...formData,
        event_id: eventId
      });
      toast.success('Enlace de vendedor creado');
      setShowModal(false);
      setFormData({ vendor_name: '', commission_percentage: 0 });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al crear enlace');
    }
  };

  const handleDelete = async (vendorId) => {
    if (!confirm('¿Eliminar este enlace de vendedor?')) return;
    try {
      await eventsApi.deleteVendorLink(eventId, vendorId);
      toast.success('Enlace eliminado');
      fetchData();
    } catch (error) {
      toast.error('Error al eliminar enlace');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Link copiado al portapapeles');
  };

  const getVendorUrl = (linkCode) => {
    return `${window.location.origin}/evento/${eventId}?ref=${linkCode}`;
  };

  if (loading) {
    return (
      <SidebarProvider>
        <AdminLayout>
          <div className="animate-pulse space-y-6">
            <div className="h-8 bg-[#1A1A1A] w-1/3 rounded" />
            <div className="h-64 bg-[#1A1A1A] rounded" />
          </div>
        </AdminLayout>
      </SidebarProvider>
    );
  }

  const totalTickets = vendors.reduce((sum, v) => sum + (v.tickets_sold || 0), 0);
  const totalRevenue = vendors.reduce((sum, v) => sum + (v.revenue_generated || 0), 0);

  return (
    <SidebarProvider>
      <AdminLayout>
        <div data-testid="event-vendors-page">
          <EventSubNav eventTitle={event?.title} />

          {/* Stats */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="grid grid-cols-3 gap-6 mb-8"
          >
            <div className="card-dashboard">
              <p className="text-[#A1A1AA] text-sm uppercase tracking-wider mb-1">Total Vendedores</p>
              <p className="text-3xl font-black">{vendors.length}</p>
            </div>
            <div className="card-dashboard">
              <p className="text-[#A1A1AA] text-sm uppercase tracking-wider mb-1">Tickets Vendidos</p>
              <p className="text-3xl font-black">{totalTickets}</p>
            </div>
            <div className="card-dashboard">
              <p className="text-[#A1A1AA] text-sm uppercase tracking-wider mb-1">Ingresos Generados</p>
              <p className="text-3xl font-black">${totalRevenue.toLocaleString()}</p>
            </div>
          </motion.div>

          {/* Header Actions */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="flex items-center justify-between mb-6"
          >
            <div>
              <h2 className="text-xl font-bold uppercase tracking-tight">Enlaces de Vendedores / PRs</h2>
              <p className="text-[#A1A1AA] text-sm mt-1">Crea links personalizados para trackear ventas por vendedor</p>
            </div>
            <Button onClick={() => setShowModal(true)} className="btn-primary">
              <Plus size={16} className="mr-2" />
              Nuevo Vendedor
            </Button>
          </motion.div>

          {/* Vendors List */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="space-y-4"
          >
            {vendors.length > 0 ? (
              vendors.map((vendor) => {
                const vendorUrl = getVendorUrl(vendor.link_code);
                return (
                  <div key={vendor.id} className="card-dashboard">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-6">
                        <div className="w-14 h-14 bg-purple-500/20 flex items-center justify-center">
                          <UserPlus size={28} className="text-purple-500" />
                        </div>
                        <div>
                          <h3 className="font-black text-xl">{vendor.vendor_name}</h3>
                          <div className="flex items-center gap-4 mt-1 text-[#A1A1AA] text-sm">
                            <span className="flex items-center gap-1">
                              <Ticket size={14} />
                              {vendor.tickets_sold || 0} tickets
                            </span>
                            <span className="flex items-center gap-1">
                              <DollarSign size={14} />
                              ${(vendor.revenue_generated || 0).toLocaleString()}
                            </span>
                            {vendor.commission_percentage > 0 && (
                              <span className="text-yellow-500">
                                {vendor.commission_percentage}% comisión
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-3">
                        {/* Link display */}
                        <div className="flex items-center bg-[#0A0A0A] border border-[#27272A]">
                          <code className="px-3 py-2 text-sm text-[#A1A1AA] max-w-xs truncate">
                            {vendorUrl}
                          </code>
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => copyToClipboard(vendorUrl)}
                            className="border-l border-[#27272A] rounded-none px-3"
                          >
                            <Copy size={16} />
                          </Button>
                          <a 
                            href={vendorUrl} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="border-l border-[#27272A] px-3 py-2 hover:bg-[#1A1A1A] transition-colors"
                          >
                            <ExternalLink size={16} className="text-[#A1A1AA]" />
                          </a>
                        </div>
                        
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => handleDelete(vendor.id)}
                          className="text-red-500 hover:text-red-400 hover:bg-red-500/10"
                        >
                          <Trash2 size={18} />
                        </Button>
                      </div>
                    </div>
                  </div>
                );
              })
            ) : (
              <div className="card-dashboard text-center py-16">
                <UserPlus size={48} className="mx-auto mb-4 text-[#27272A]" />
                <p className="text-[#52525B] text-lg">No hay enlaces de vendedores</p>
                <p className="text-[#52525B] text-sm mt-2">
                  Crea enlaces únicos para que tus vendedores o PRs puedan trackear sus ventas
                </p>
                <Button onClick={() => setShowModal(true)} className="btn-secondary mt-6">
                  <Plus size={16} className="mr-2" />
                  Crear Primer Enlace
                </Button>
              </div>
            )}
          </motion.div>
        </div>

      {/* Create Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <motion.div 
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-[#121212] border border-[#27272A] p-6 w-full max-w-md"
          >
            <h3 className="text-xl font-bold uppercase mb-6">Nuevo Enlace de Vendedor</h3>
            
            <div className="space-y-4">
              <div>
                <Label className="text-xs uppercase tracking-widest text-[#A1A1AA] mb-2 block">
                  Nombre del Vendedor / PR
                </Label>
                <input
                  value={formData.vendor_name}
                  onChange={(e) => setFormData({...formData, vendor_name: e.target.value})}
                  placeholder="Juan Pérez"
                  className="w-full bg-transparent border-b border-[#27272A] focus:border-white px-0 py-3 text-white outline-none text-lg"
                />
              </div>
              
              <div>
                <Label className="text-xs uppercase tracking-widest text-[#A1A1AA] mb-2 block">
                  Comisión (%)
                </Label>
                <input
                  type="number"
                  value={formData.commission_percentage}
                  onChange={(e) => setFormData({...formData, commission_percentage: parseFloat(e.target.value) || 0})}
                  placeholder="0"
                  className="w-full bg-transparent border-b border-[#27272A] focus:border-white px-0 py-3 text-white outline-none"
                />
                <p className="text-[#52525B] text-xs mt-2">
                  Opcional: porcentaje de comisión por cada venta
                </p>
              </div>
            </div>
            
            <div className="flex gap-3 mt-8">
              <Button variant="outline" onClick={() => setShowModal(false)} className="flex-1 border-[#27272A]">
                Cancelar
              </Button>
              <Button onClick={handleCreate} className="flex-1 btn-primary">
                Crear Enlace
              </Button>
            </div>
          </motion.div>
        </div>
      )}
        </AdminLayout>
      </SidebarProvider>
  );
};

export default EventVendorsPage;
